import { assertValue } from '@shared/helpers'
import {
  LoanApplicationDecision,
  LoanApplicationResultResponse,
  SecLoanApplication,
  SecLoanApplicationType,
  SecLoanPurposeType,
  SecLoansApplicationMetadata,
  SecLoansGetApplicationResponse,
  SecLoansParam,
  SecLoansUserInfoResponse,
} from '@shared/sec-loans'
import { ConfigResponse } from '@shared/types'
import { getMachineStorageKey } from '@ui-common/hooks'
import { AuthenticatedEvent, ContinueEvent } from '@ui-common/state'
import { AuthenticationCancelledEvent } from '@ui-common/steps/AuthenticateStep/types'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep'
import { ExitWizardEvent, FinishedEvent, NextStepEvent } from '@ui-common/types'
import { removeSessionStorageItem } from '@ui-common/utils'
import { assign, setup } from 'xstate'

import { OnAutomaticOfferData } from '../loan-offer/types'
import { isPrimaryApplicantWaitingSecondaryApplicant } from '../utils/helpers'
import {
  APPLICANT_INFO_WIZARD_MACHINE_ID,
  ApplicantInfoPage,
} from './steps/applicant-info-phase/applicantInfoWizardMachine'
import { LOAN_INFO_WIZARD_MACHINE_ID, LoanInfoPage } from './steps/loan-info-phase/loanInfoWizardMachine'
import { TARGET_INFO_WIZARD_MACHINE_ID, TargetInfoPage } from './steps/loan-target-phase/targetInfoWizardMachine'
import { BaseWizardContext, SecLoansApplicationData, User, WizardData } from './types'
import { getAppData } from './wizardData'
import { PhaseStepType, PhaseType } from './wizardSteps'

export const PHASE_TARGET_INFO = 'PhaseTargetInfo'
export const PHASE_APPLICANT_INFO = 'PhaseApplicantInfo'
export const PHASE_LOAN_INFO = 'PhaseLoanInfo'

export const STEP_ENTRY = 'STEP_ENTRY'
export const STEP_GET_APPLICATION = 'GetApplicationStep'
export const STEP_STATUS = 'StepStatus'
export const STEP_SEND = 'StepSend'
export const STEP_THE_END = 'StepTheEnd'
export const STEP_SUCCEEDED = 'SucceededStep'
export const STEP_ABORTED = 'AbortedStep'
export const STEP_CANCEL_APPLICATION = 'StepCancelApplication'
export const STEP_RETURN_APPLICATION = 'StepReturnApplication'

export type GoToStepEvent = {
  type: 'GOTO_STEP'
  phase: PhaseType
  step?: PhaseStepType
  isSecondApplicantMode?: boolean
}

type GotoTheEndEvent = {
  type: 'GOTO_THE_END'
}

type SectionDoneEvent = {
  type: 'SECTION_DONE'
}

type ApplicationSaveEvent = {
  type: 'SAVE_APPLICATION'
  data: SecLoanApplication
}

type BackToStatusEvent = {
  type: 'BACK_TO_STATUS'
}

export type SentModalConfirmedEvent = {
  type: 'SENT_MODAL_CONFIRMED'
}

export type GetApplicationsAndStartOverEvent = {
  type: 'GET_APPLICATIONS_AND_START_OVER'
}

export type CancelApplicationEvent = {
  type: 'CANCEL_APPLICATION'
}

export type ReturnApplicationEvent = {
  type: 'RETURN_APPLICATION'
}

export type SendSuccessEvent = {
  type: 'SEND_SUCCESS'
  data: LoanApplicationResultResponse
}

export type OfferFoundEvent = {
  type: 'OFFER_FOUND'
  data: OnAutomaticOfferData
}

export type SendFailedEvent = {
  type: 'SEND_FAILED'
}

export type RemoveSendErrorEvent = {
  type: 'REMOVE_SEND_ERROR'
}

type WizardEvent =
  | ExitWizardEvent
  | ContinueEvent
  | NextStepEvent<unknown>
  | GoToStepEvent
  | SectionDoneEvent
  | AuthenticatedEvent
  | AuthenticationCancelledEvent
  | SendSuccessEvent
  | SendFailedEvent
  | SentModalConfirmedEvent
  | OfferFoundEvent
  | RemoveSendErrorEvent
  | GetDataDoneEvent<SecLoansUserInfoResponse>
  | GetDataDoneEvent<SecLoansGetApplicationResponse>
  | GetDataDoneEvent<SecLoansApplicationMetadata[]>
  | BackToStatusEvent
  | ApplicationSaveEvent
  | GotoTheEndEvent
  | GetApplicationsAndStartOverEvent
  | CancelApplicationEvent
  | ReturnApplicationEvent
  | FinishedEvent

export interface ValidationParams {
  minimumHousingLoanAmount: number
  minimumPurchaseLoanAmount: number
}

export interface WizardContext extends BaseWizardContext, SecLoansApplicationData, ConfigResponse<SecLoansParam> {
  data: WizardData
  authToken?: string
  stepToEdit?: TargetInfoPage | ApplicantInfoPage | LoanInfoPage
  user?: User
  loanApplicationStatusData?: LoanApplicationResultResponse
  isSecondApplicantMode: boolean
  shouldShowSentModal?: boolean
  hasSendError: boolean
  validationParams?: ValidationParams
  isNewApplication?: boolean
  applicationType?: SecLoanApplicationType
  applicationPurpose?: SecLoanPurposeType
  applicationId?: string
}

export const initialSecLoansWizardContext: WizardContext = {
  data: {},
  user: undefined,
  isNewApplication: false,
  applicationMetadata: undefined,
  loanApplicationStatusData: undefined,
  isSecondApplicantMode: false,
  shouldShowSentModal: false,
  hasSendError: false,
}

const wizardMachineId = 'secLoansWizardMachine'

export const clearSecLoansWizardMachineSessionStorage = (): void =>
  removeSessionStorageItem(getMachineStorageKey(wizardMachineId))

export const createWizardMachine = ({
  onGoToCatalog,
  onNewApplicationClick,
  onAutomaticOfferResponse,
}: {
  onGoToCatalog: () => void
  onNewApplicationClick: () => void
  onAutomaticOfferResponse: (data: OnAutomaticOfferData) => void
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
}) => {
  return setup({
    types: {
      context: {} as WizardContext,
      events: {} as WizardEvent,
      input: {} as WizardContext,
    },
    actions: {
      saveEdit: assign(
        (_, options: { step?: TargetInfoPage | ApplicantInfoPage | LoanInfoPage; isSecondApplicantMode: boolean }) => {
          const { step, isSecondApplicantMode } = options
          return {
            stepToEdit: step,
            isSecondApplicantMode,
          }
        },
      ),
      storeBasicInfo: assign((_, user: SecLoansUserInfoResponse) => {
        return { user }
      }),
      storeApplication: assign(({ context }, data: SecLoansGetApplicationResponse) => {
        return {
          applicationData: { ...data },
          applicationMetadata: { ...data },
          loanApplicationStatusData: data.applicationResult,
          applicationType: data.applicationType,
          applicationId: data.applicationId,
          applicationPurpose: data.applicationPurpose,
          data: getAppData(
            data.data,
            data,
            assertValue(context.validationParams, 'context.validationParams'),
            data.attachments,
          ),
        }
      }),
      saveApplication: assign(({ context }, data: SecLoanApplication) => {
        return {
          stepToEdit: undefined,
          data: getAppData(
            data.data,
            data,
            assertValue(context.validationParams, 'context.validationParams'),
            data.attachments,
          ),
        }
      }),
      saveAllApplications: assign(({ context }, data: SecLoansApplicationMetadata[]) => {
        return {
          user: {
            ...assertValue(context.user, 'context.user'),
            loanApplications: data,
          },
        }
      }),
      saveLoanStatus: assign((_, data: LoanApplicationResultResponse) => {
        return { loanApplicationStatusData: data }
      }),
      redirectToOffer: (_, data: OnAutomaticOfferData) =>
        onAutomaticOfferResponse({
          loanOffer: assertValue(data.loanOffer, 'data.loanOffer'),
        }),
      saveSendError: assign(() => {
        return { hasSendError: true }
      }),
      removeSendError: assign(() => {
        return { hasSendError: false }
      }),
      disableSecondApplicantMode: assign(() => {
        return {
          isSecondApplicantMode: false,
        }
      }),
      setShouldShowSentModal: assign((_, shouldShowSentModal: boolean) => {
        return {
          shouldShowSentModal,
        }
      }),
      clearLoanApplicationData: assign(({ context }) => {
        return {
          ...initialSecLoansWizardContext,
          user: context.user,
          authToken: context.authToken,
        }
      }),
    },
    guards: {
      isGotoTargetInfoEvent: (_, phase: string) => {
        return phase === TARGET_INFO_WIZARD_MACHINE_ID
      },
      isGotoApplicantInfoEvent: (_, phase: string) => {
        return phase === APPLICANT_INFO_WIZARD_MACHINE_ID
      },
      isGotoLoanInfoEvent: (_, phase: string) => {
        return phase === LOAN_INFO_WIZARD_MACHINE_ID
      },
      isPrimaryApplicantWaitingSecondaryApplicant: (_, data: SecLoansGetApplicationResponse) => {
        const { applicantRole, applicationState } = data
        return isPrimaryApplicantWaitingSecondaryApplicant(applicantRole, applicationState)
      },
      isSentApplication: (_, data: SecLoansGetApplicationResponse) => {
        return (
          !!data.applicationResult && data.applicationResult.decision !== LoanApplicationDecision.WaitingForCoApplicant
        )
      },
    },
  }).createMachine({
    id: wizardMachineId,
    predictableActionArguments: true,
    initial: STEP_ENTRY,
    context: ({ input }) => ({ ...initialSecLoansWizardContext, ...input }),
    states: {
      STEP_ENTRY: {
        id: STEP_ENTRY,
        always: [
          {
            guard: ({ context }) => !!context.isNewApplication,
            target: STEP_STATUS,
          },
          {
            target: STEP_GET_APPLICATION,
          },
        ],
      },
      [STEP_GET_APPLICATION]: {
        id: STEP_GET_APPLICATION,
        on: {
          GET_DATA_DONE: [
            {
              guard: {
                type: 'isPrimaryApplicantWaitingSecondaryApplicant',
                params: ({ event }) => event.data as SecLoansGetApplicationResponse,
              },
              target: STEP_THE_END,
              actions: {
                type: 'storeApplication',
                params: ({ event }) => event.data as SecLoansGetApplicationResponse,
              },
            },
            {
              guard: { type: 'isSentApplication', params: ({ event }) => event.data as SecLoansGetApplicationResponse },
              target: STEP_THE_END,
              actions: {
                type: 'storeApplication',
                params: ({ event }) => event.data as SecLoansGetApplicationResponse,
              },
            },
            {
              target: STEP_STATUS,
              actions: {
                type: 'storeApplication',
                params: ({ event }) => event.data as SecLoansGetApplicationResponse,
              },
            },
          ],
        },
      },
      [STEP_STATUS]: {
        id: STEP_STATUS,
        on: {
          NEXT_STEP: { target: PHASE_TARGET_INFO },
          GOTO_STEP: [
            {
              target: PHASE_TARGET_INFO,
              guard: { type: 'isGotoTargetInfoEvent', params: ({ event }) => event.phase },
              actions: {
                type: 'saveEdit',
                params: ({ event }) => ({
                  isSecondApplicantMode: !!event.isSecondApplicantMode,
                  step: event.step,
                }),
              },
            },
            {
              target: PHASE_APPLICANT_INFO,
              guard: { type: 'isGotoApplicantInfoEvent', params: ({ event }) => event.phase },
              actions: {
                type: 'saveEdit',
                params: ({ event }) => ({
                  step: event.step,
                  isSecondApplicantMode: !!event.isSecondApplicantMode,
                }),
              },
            },
            {
              target: PHASE_LOAN_INFO,
              guard: { type: 'isGotoLoanInfoEvent', params: ({ event }) => event.phase },
              actions: {
                type: 'saveEdit',
                params: ({ event }) => ({
                  step: event.step,
                  isSecondApplicantMode: !!event.isSecondApplicantMode,
                }),
              },
            },
          ],
          CONTINUE: {
            target: STEP_SEND,
            actions: {
              type: 'setShouldShowSentModal',
              params: () => true,
            },
          },
          CANCEL_APPLICATION: { target: STEP_CANCEL_APPLICATION, actions: { type: 'clearLoanApplicationData' } },
          RETURN_APPLICATION: { target: STEP_RETURN_APPLICATION, actions: { type: 'clearLoanApplicationData' } },
          REMOVE_SEND_ERROR: { actions: 'removeSendError' },
        },
      },
      [PHASE_TARGET_INFO]: {
        id: PHASE_TARGET_INFO,
        on: {
          SECTION_DONE: { target: STEP_STATUS },
        },
      },
      [PHASE_APPLICANT_INFO]: {
        id: PHASE_APPLICANT_INFO,
        on: {
          SECTION_DONE: { target: STEP_STATUS, actions: { type: 'disableSecondApplicantMode' } },
        },
      },
      [PHASE_LOAN_INFO]: {
        id: PHASE_LOAN_INFO,
        on: {
          SECTION_DONE: { target: STEP_STATUS },
        },
      },
      [STEP_SEND]: {
        id: STEP_SEND,
        on: {
          OFFER_FOUND: [
            {
              actions: { type: 'redirectToOffer', params: ({ event }) => event.data as OnAutomaticOfferData },
            },
          ],
          SEND_SUCCESS: [
            {
              target: STEP_THE_END,
              actions: { type: 'saveLoanStatus', params: ({ event }) => event.data as LoanApplicationResultResponse },
            },
          ],
          SEND_FAILED: [
            {
              target: STEP_STATUS,
              actions: { type: 'saveSendError' },
            },
          ],
        },
      },
      [STEP_CANCEL_APPLICATION]: {
        id: STEP_CANCEL_APPLICATION,
        on: {
          GET_DATA_DONE: {
            actions: [onGoToCatalog],
          },
        },
      },
      [STEP_RETURN_APPLICATION]: {
        id: STEP_RETURN_APPLICATION,
        on: {
          GET_DATA_DONE: {
            actions: [onGoToCatalog],
          },
        },
      },
      [STEP_THE_END]: {
        id: STEP_THE_END,
        on: {
          SENT_MODAL_CONFIRMED: {
            actions: {
              type: 'setShouldShowSentModal',
              params: () => false,
            },
          },
          FINISHED: { target: STEP_SUCCEEDED },
        },
      },
      [STEP_SUCCEEDED]: {
        id: STEP_SUCCEEDED,
        type: 'final',
      },
      [STEP_ABORTED]: {
        id: STEP_ABORTED,
        type: 'final',
      },
    },
    on: {
      EXIT_WIZARD: { target: `.${STEP_ABORTED}` },
      SAVE_APPLICATION: { actions: { type: 'saveApplication', params: ({ event }) => event.data } },
      BACK_TO_STATUS: { target: `.${STEP_STATUS}` },
      GET_APPLICATIONS_AND_START_OVER: {
        actions: [onGoToCatalog],
      },
      NEW_APPLICATION: {
        actions: [onNewApplicationClick],
      },
    },
  })
}
